import { SearchOutlined, CloseCircleFilled } from "@ant-design/icons";
import { Input } from "antd";
import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { usePlaylistContext } from "../../context/PlaylistContext";
import { SearchBarStyle } from "./SearchBar.style";

const AUTO_SEARCH_DELAY_MS = 400;

const SearchBar = () => {

  const { dispatch, state } = usePlaylistContext();

  const [search, setSearch] = useState("");
  const autoSearchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const isOnSearchPage = location.pathname === "/search";

  // Sync local search state with context when navigating to search page with query.
  // Compare trimmed so a trailing space the user is still typing is not stripped.
  useEffect(() => {
    if (state.searchSong !== search.trim()) {
      setSearch(state.searchSong);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.searchSong]);

  useEffect(
    () => () => {
      if (autoSearchTimerRef.current) clearTimeout(autoSearchTimerRef.current);
    },
    []
  );

  const clearResults = () => {
    dispatch({
      type: "SET_SEARCHED_SONG",
      payload: '',
    });
    dispatch({
      type: "SET_SONGS_SEARCH_LIST",
      payload: [],
    });
    dispatch({
      type: "SET_SEARCH_TRACKS_TOTAL",
      payload: null,
    });
    if (isOnSearchPage) {
      const params = new URLSearchParams(location.search);
      params.delete("q");
      const query = params.toString();
      navigate("/search" + (query ? "?" + query : ""), { replace: true });
    }
  };

  const runSearch = (value: string, { replace }: { replace: boolean }) => {
    const searchTerm = value.trim();
    if (!searchTerm) return;

    dispatch({
      type: "SET_SEARCHED_SONG",
      payload: searchTerm,
    });

    let playlist = window.location.pathname.split("/");
    let bulk = localStorage.getItem('bulk');

    let targetPath = "";

    if (isOnSearchPage) {
      const params = new URLSearchParams(location.search);
      params.set("q", searchTerm);
      targetPath = "/search?" + params.toString();
    } else if (playlist[1] === "playlist" && bulk) {
      targetPath = "/search?playlist=" + playlist[2] + "&q=" + encodeURIComponent(searchTerm);
    } else {
      targetPath = "/search?q=" + encodeURIComponent(searchTerm);
    }
    navigate(targetPath, { replace });
  };

  const handleChange = (e) => {
    const value = e.target.value;
    setSearch(value);

    // Search-as-you-type only on Discover Music; elsewhere Enter still navigates to it.
    if (!isOnSearchPage) return;

    if (autoSearchTimerRef.current) clearTimeout(autoSearchTimerRef.current);

    if (!value.trim()) {
      clearResults();
      return;
    }

    autoSearchTimerRef.current = setTimeout(() => {
      runSearch(value, { replace: true });
    }, AUTO_SEARCH_DELAY_MS);
  };

  const handleSearch = () => {
    if (autoSearchTimerRef.current) clearTimeout(autoSearchTimerRef.current);
    runSearch(search, { replace: isOnSearchPage });
  };

  const handleInputClick = (value) => {
    if (value.key !== "Enter") return;
    handleSearch();
  };

  const handleClear = () => {
    if (autoSearchTimerRef.current) clearTimeout(autoSearchTimerRef.current);
    setSearch('');
    clearResults();
  };

  return (
    <SearchBarStyle>
      <Input
        className="searchBar"
        type="text"
        placeholder="Browse for songs, artists, or podcasts..."
        value={search}
        onChange={handleChange}
        onKeyDown={handleInputClick}
        suffix={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {search && (
              <CloseCircleFilled 
                onClick={handleClear}
                style={{ 
                  cursor: 'pointer', 
                  fontSize: '14px',
                  opacity: 0.5,
                  transition: 'opacity 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                onMouseLeave={(e) => e.currentTarget.style.opacity = '0.5'}
              />
            )}
            <SearchOutlined 
              onClick={handleSearch}
              style={{ 
                cursor: 'pointer',
                fontSize: '14px',
                opacity: 0.5,
                transition: 'opacity 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
              onMouseLeave={(e) => e.currentTarget.style.opacity = '0.5'}
            />
          </div>
        }
      />
    </SearchBarStyle>
  );
};

export default SearchBar;
